import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  type Cell,
  type VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { DocumentVersionSummary } from "~/api/types";
import { CenterSpinner } from "~/atoms/spinner";
import {
  allPageSizeParamValue,
  getVisiblePageNumbers,
} from "~/helpers/pagination";
import { DataTable } from "~/molecules/DataTable";
import { TablePagination } from "~/molecules/TablePagination";
import NoResult from "~/molecules/NoResult";
import { useUserContext } from "~/hooks/useUserContext";
import { featureFlags } from "~/config/featureFlags";
import { cn } from "~/lib/utils";
import { FilterBar, FiltersSidebar } from "./FilterBar";
import { FieldEditDialog } from "./FieldEditDialog";
import { Toolbar } from "./Toolbar";
import {
  createDocumentsColumns,
  getDocumentsListColumnSpecs,
  getEditableFieldByColumnId,
  resolveDocumentsColumnMenu,
} from "./columnRegistry";
import { isEditableField } from "~/helpers/libraryEditing";
import type { ActiveFieldEdit } from "~/helpers/libraryTypes";
import type { DocumentVersionsListModel } from "~/hooks/useDocumentVersionsListModel";

interface DocumentVersionsListProps {
  model: DocumentVersionsListModel;
}

export const DocumentVersionsList = ({ model }: DocumentVersionsListProps) => {
  const { t } = useTranslation();
  const isReferenceNumberEnabled =
    useFeatureFlagEnabled(featureFlags.referenceNumber) ?? false;
  const isEditingEnabled = useFeatureFlagEnabled(featureFlags.bulk) ?? false;
  const { user } = useUserContext();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [activeFieldEdit, setActiveFieldEdit] =
    useState<ActiveFieldEdit | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const tagLabelById = model.filters.multiSelectFieldData.tag.labelsByValue;
  const handleFieldEditClick = useCallback((params: ActiveFieldEdit) => {
    setActiveFieldEdit(params);
  }, []);
  const columnSpecs = useMemo(
    () =>
      getDocumentsListColumnSpecs({
        referenceNumber: isReferenceNumberEnabled,
      }),
    [isReferenceNumberEnabled],
  );
  const columns = useMemo(
    () =>
      createDocumentsColumns({
        t,
        currentUserId: user.id,
        tagLabelById,
        columnSpecs,
      }),
    [columnSpecs, t, user.id, tagLabelById],
  );
  const editableFieldByColumnId = useMemo(
    () => getEditableFieldByColumnId(columnSpecs),
    [columnSpecs],
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- acknowledged; not using React Compiler
  const table = useReactTable({
    data: model.filteredDocumentVersions,
    columns,
    state: {
      sorting: model.sorting,
      columnVisibility,
      pagination: model.pagination,
    },
    onSortingChange: model.handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: model.handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id.toString(),
    autoResetPageIndex: false,
  });
  const getCellProps = useCallback(
    (
      cell: Cell<DocumentVersionSummary, unknown>,
      rowData: DocumentVersionSummary,
    ) => {
      const editableField = editableFieldByColumnId[cell.column.id];
      const canEditField =
        isEditingEnabled && isEditableField(rowData, editableField);

      if (!canEditField || !editableField) {
        return undefined;
      }

      return {
        className:
          "cursor-pointer rounded-md transition-shadow hover:shadow-[inset_0_0_0_1px_hsl(var(--primary))]",
        onClick: () =>
          handleFieldEditClick({
            documentId: rowData.document.id,
            versionId: rowData.id,
            field: editableField,
          }),
      };
    },
    [editableFieldByColumnId, handleFieldEditClick, isEditingEnabled],
  );

  if (model.isLoading) {
    return <CenterSpinner withText />;
  }

  if (!model.hasData) {
    return <NoResult message={t(($) => $.library.noDocumentVersionsFound)} />;
  }

  const { columnMenuLabels, resolvedGroups, ungroupedColumns } =
    resolveDocumentsColumnMenu({
      columnSpecs,
      table,
      t,
    });
  const currentPage = table.getState().pagination.pageIndex + 1;
  const visiblePageNumbers = getVisiblePageNumbers(
    currentPage,
    table.getPageCount(),
  );

  return (
    <div className="space-y-4">
      <FieldEditDialog
        activeEdit={activeFieldEdit}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setActiveFieldEdit(null);
          }
        }}
        onSaved={async () => {
          await model.mutate();
        }}
      />
      <Toolbar
        isFiltersOpen={isFiltersOpen}
        onToggleFilters={() => setIsFiltersOpen((current) => !current)}
        titleSearch={model.titleSearch}
        onTitleSearchChange={model.handleTitleSearchChange}
        titlePlaceholder={t(($) => $.library.filterByTitlePlaceholder)}
        columnsButtonLabel={t(($) => $.library.columns)}
        ungroupedColumns={ungroupedColumns}
        resolvedGroups={resolvedGroups}
        columnMenuLabels={columnMenuLabels}
      />

      <div
        className={cn("flex items-start", isFiltersOpen ? "gap-4" : "gap-0")}
      >
        <aside
          className={cn(
            "shrink-0 overflow-hidden transition-[width,opacity] duration-200",
            isFiltersOpen ? "w-80 opacity-100" : "w-0 opacity-0",
          )}
        >
          <div
            className={cn(
              "h-full w-80 origin-left transition-transform duration-200",
              isFiltersOpen ? "scale-x-100" : "pointer-events-none scale-x-95",
            )}
          >
            <FiltersSidebar filters={model.filters} />
          </div>
        </aside>

        <div className="min-w-0 flex-1 space-y-4">
          <FilterBar filters={model.filters} />
          <DataTable
            table={table}
            noResultsLabel={t(($) => $.library.noResults)}
            getCellProps={getCellProps}
          />
          <TablePagination
            table={table}
            currentPage={currentPage}
            visiblePageNumbers={visiblePageNumbers}
            pageSizeValue={
              model.isAllPageSizeMode
                ? allPageSizeParamValue
                : table.getState().pagination.pageSize.toString()
            }
            onPageSizeChange={model.handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};
